// src/hooks/usePWA.js
import { useState, useEffect } from 'react';

/**
 * Hook que gestiona toda la lógica PWA:
 *  - Ocultar el splash screen nativo al montar la app
 *  - Detectar si la app puede instalarse (Android/Chrome)
 *  - Detectar si ya está instalada (modo standalone)
 *  - Detectar actualizaciones del Service Worker
 *  - Detectar si hay conexión a internet
 */
export function usePWA() {
  const [installable, setInstallable]     = useState(false);
  const [isStandalone, setIsStandalone]   = useState(false);
  const [updateAvailable, setUpdate]      = useState(false);
  const [isOnline, setIsOnline]           = useState(navigator.onLine);

  useEffect(() => {
    // ── Ocultar splash nativo ─────────────────────────────────────────────
    if (window.__gr_hide_splash) {
      // Pequeño delay para que la transición se vea suave
      const t = setTimeout(window.__gr_hide_splash, 300);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    // ── ¿Está corriendo como app instalada? ───────────────────────────────
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // ── Prompt de instalación ya capturado antes de que React montara ─────
    if (window.__gr_install_prompt) setInstallable(true);

    // ── Escuchar el evento si llega después de montar ─────────────────────
    const onInstallable = () => setInstallable(true);
    window.addEventListener('pwa-installable', onInstallable);

    // ── Notificación de actualización del SW ──────────────────────────────
    window.__gr_notify_update = () => setUpdate(true);

    // ── Conectividad ──────────────────────────────────────────────────────
    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('pwa-installable', onInstallable);
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // ── Disparar el prompt de instalación ─────────────────────────────────
  const promptInstall = async () => {
    const prompt = window.__gr_install_prompt;
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      window.__gr_install_prompt = null;
      setInstallable(false);
    }
    return outcome === 'accepted';
  };

  // ── Aplicar actualización: recargar para activar el nuevo SW ──────────
  const applyUpdate = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  };

  return { installable, isStandalone, updateAvailable, isOnline, promptInstall, applyUpdate };
}
